import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { downloadBlob, PDF_MIME } from '../../lib/download';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { EmailDocButton } from '../../components/EmailDocButton';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard } from '../../components/DataTable';
import { StatusPill } from '../../components/StatusPill';
import { FirmnessBadge } from '../../components/FirmnessBadge';
import { formatDayLabel, groupByDay, taskTone, taskStatusLabel } from './opsShared';

interface SupRow {
  taskId: string;
  status: string;
  qty: number;
  orderId: string;
  invoice: string;
  shop: string;
  customer: string;
  phone: string;
  product: string;
  sku: string;
  color: string | null;
  sideColor: string | null;
  fabric: string | null;
  density: string | null;
  supplierName: string | null;
  poReference: string | null;
  supplierNote: string | null;
  sentAt: string | null;
  readyAt: string | null;
  deliveryDate: string | null;
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'PENDING', label: 'To send' },
  { key: 'SENT', label: 'Sent' },
  { key: 'READY', label: 'Ready' },
] as const;

type ModalState = { row: SupRow; mode: 'send' | 'edit' } | null;

export function SupplierPage(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const list = useQuery({
    queryKey: ['ops-supplier'],
    queryFn: () => apiGet<SupRow[]>('/operations/supplier'),
  });

  const update = useMutation({
    mutationFn: (vars: { taskId: string; body: Record<string, unknown> }) =>
      apiPost(`/operations/supplier/${vars.taskId}/status`, vars.body),
    onSuccess: () => {
      setErr(null);
      setModal(null);
      qc.invalidateQueries({ queryKey: ['ops-supplier'] });
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const counts = useMemo(() => {
    const rows = list.data ?? [];
    return {
      all: rows.length,
      PENDING: rows.filter((r) => r.status === 'PENDING').length,
      SENT: rows.filter((r) => r.status === 'SENT').length,
      READY: rows.filter((r) => r.status === 'READY').length,
    };
  }, [list.data]);

  const filtered = useMemo(() => {
    let rows = list.data ?? [];
    if (tab !== 'all') rows = rows.filter((r) => r.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.customer.toLowerCase().includes(q) ||
          r.invoice.toLowerCase().includes(q) ||
          r.product.toLowerCase().includes(q) ||
          (r.supplierName ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [list.data, tab, search]);

  const groups = useMemo(() => groupByDay(filtered, (r) => r.deliveryDate), [filtered]);

  const downloadDay = (day: string | null): void => {
    if (!day) return;
    void downloadBlob(
      '/operations/supplier/day-sheet',
      { date: day },
      PDF_MIME,
      `purchase-orders-${day}.pdf`,
    );
  };

  return (
    <PageScaffold
      scroll={false}
      title="Purchase orders"
      subtitle={
        <>
          Supplier-made items (e.g. bed bases) to order in. Mark each <strong>Sent</strong> when the
          PO goes out, then <strong>Ready</strong> when it arrives — only then does the order move
          to delivery.
        </>
      }
    >
      {err && (
        <div className="shrink-0 rounded border border-negative/30 bg-negative-soft text-negative text-sm px-4 py-2">
          {err}
        </div>
      )}

      <div className="shrink-0 flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'px-3 py-1.5 rounded text-sm transition ' +
                (tab === t.key
                  ? 'bg-accent-soft text-accent font-medium'
                  : 'text-ink-2 hover:bg-paper-2')
              }
            >
              {t.label}
              <span className="ml-1.5 text-xs text-ink-3">{counts[t.key]}</span>
            </button>
          ))}
        </div>
        <div className="w-64">
          <Input
            placeholder="Search supplier, customer, invoice…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <DataTableCard scrollClassName="space-y-6 p-px">
        {list.isLoading ? (
          <div className="text-center text-ink-3 py-10">Loading…</div>
        ) : groups.length === 0 ? (
          <EmptyState
            title="Nothing to order"
            description="Confirmed supplier-made items will appear here, sorted by delivery date."
          />
        ) : (
          groups.map((g) => (
            <Card
              key={g.day ?? 'unscheduled'}
              title={formatDayLabel(g.day)}
              subtitle={`${g.rows.length} item${g.rows.length === 1 ? '' : 's'} · deliver by this date`}
              bodyClassName="p-0"
              action={
                g.day ? (
                  <div className="flex items-center gap-1">
                    <Button variant="secondary" size="sm" onClick={() => downloadDay(g.day)}>
                      PO sheet · PDF
                    </Button>
                    <EmailDocButton
                      endpoint="/operations/supplier/day-sheet"
                      method="get"
                      params={{ date: g.day }}
                      selfDefault
                      buttonVariant="secondary"
                      buttonSize="sm"
                      title="Email purchase order sheet"
                      subtitle="Send the purchase order worksheet PDF by email."
                    />
                  </div>
                ) : undefined
              }
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Invoice</th>
                    <th className="px-6 py-2 font-medium">Product</th>
                    <th className="px-6 py-2 font-medium text-right">Qty</th>
                    <th className="px-6 py-2 font-medium">Supplier / PO</th>
                    <th className="px-6 py-2 font-medium">Status</th>
                    <th className="px-6 py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.taskId} className="border-b border-rule-2">
                      <td className="px-6 py-3">
                        <div className="kc-mono text-xs">{r.invoice}</div>
                        <div className="text-xs text-ink-3">{r.customer}</div>
                      </td>
                      <td className="px-6 py-3">
                        {r.product}
                        <div className="text-xs text-ink-3 kc-mono">{r.sku}</div>
                        {(r.color || r.sideColor || r.fabric) && (
                          <div className="text-[11px] text-ink-3">
                            {[r.color, r.sideColor, r.fabric].filter(Boolean).join(' / ')}
                          </div>
                        )}
                        <FirmnessBadge value={r.density} className="mt-0.5" />
                      </td>
                      <td className="px-6 py-3 text-right kc-mono">{r.qty}</td>
                      <td className="px-6 py-3">
                        {r.supplierName ? (
                          <>
                            <div>{r.supplierName}</div>
                            {r.poReference && (
                              <div className="text-xs text-ink-3 kc-mono">PO {r.poReference}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-ink-3">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <StatusPill tone={taskTone(r.status)}>
                          {taskStatusLabel(r.status, 'SUPPLIER')}
                        </StatusPill>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {r.status === 'PENDING' && (
                            <Button size="sm" onClick={() => setModal({ row: r, mode: 'send' })}>
                              Send to supplier
                            </Button>
                          )}
                          {r.status === 'SENT' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setModal({ row: r, mode: 'edit' })}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                loading={update.isPending && update.variables?.taskId === r.taskId}
                                onClick={() =>
                                  update.mutate({ taskId: r.taskId, body: { status: 'READY' } })
                                }
                              >
                                Mark ready
                              </Button>
                            </>
                          )}
                          {r.status === 'READY' && (
                            <span className="text-xs text-positive">✓ received</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))
        )}
      </DataTableCard>

      {modal && (
        <SupplierModal
          state={modal}
          busy={update.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) => update.mutate({ taskId: modal.row.taskId, body })}
        />
      )}
    </PageScaffold>
  );
}

function SupplierModal({
  state,
  busy,
  onClose,
  onSubmit,
}: {
  state: NonNullable<ModalState>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}): JSX.Element {
  const { row, mode } = state;
  const [supplierName, setSupplierName] = useState(row.supplierName ?? '');
  const [poReference, setPoReference] = useState(row.poReference ?? '');
  const [note, setNote] = useState(row.supplierNote ?? '');

  const submit = (): void => {
    const body: Record<string, unknown> = {
      supplierName: supplierName.trim() || undefined,
      poReference: poReference.trim() || undefined,
      supplierNote: note.trim() || undefined,
    };
    if (mode === 'send') body.status = 'SENT';
    onSubmit(body);
  };

  return (
    <Modal
      title={mode === 'send' ? 'Send to supplier' : 'Edit supplier details'}
      subtitle={`${row.product} · ${row.qty} × · invoice ${row.invoice}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <Input
          label="Supplier"
          placeholder="e.g. AmberBed Ltd"
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
          autoFocus
        />
        <Input
          label="PO reference (optional)"
          placeholder="e.g. PO-2026-0042"
          value={poReference}
          onChange={(e) => setPoReference(e.target.value)}
        />
        <Input
          label="Note (optional)"
          placeholder="anything the supplier needs to know"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="flex gap-2 mt-5">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button className="flex-1" loading={busy} onClick={submit}>
          {mode === 'send' ? 'Mark sent' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}
